import React, { useState, useEffect } from "react";
import { Box, Container } from "@mui/material";
import { useParams, useSearchParams } from "react-router-dom";
import { useAppNavigate } from "../../../../hooks/useAppNavigate";
import { ThemeProvider, Theme, StyledEngineProvider } from "@mui/material/styles";
import { StepperWizard } from "../../../../core/components/wizard/StepperWizard";
import WizardStep from "../../../../core/components/wizard/WizardStep";
import { verbalTheme } from "../../../layout/themes/verbal.theme";
import { BranchedStoryType, getById } from "../../../../client/api/branchedStory";
import Spinner from "../../../layout/Spinner";
import Error from "../../../../core/components/Error";
import { BranchedStoryEditProvider } from "../contexts/BranchedStoryEditContext";
import { EditStoryDetails } from "./steps/EditStoryDetails";
import { EditStoryParts } from "./steps/EditStoryParts";
import { EditDecisionPoints } from "./steps/EditDecisionPoints";
import { EditQuestions } from "./steps/EditQuestions";
import { ReviewChanges } from "./steps/ReviewChanges";
import Breadcrumb from "../../../../core/components/Breadcrumb";

declare module "@mui/styles/defaultTheme" {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme {}
}

export const EditBranchedStoryWizard: React.FC = () => {
    const { push } = useAppNavigate();
    const params = useParams();
    const id = params.id!;
    const storyId = parseInt(id || "0", 10);
    const [searchParams] = useSearchParams();
    const initialStep = parseInt(searchParams.get("step") || "0", 10);
    const [activeStep, setActiveStep] = useState(initialStep);
    const [completedSteps, setCompletedSteps] = useState<boolean[]>([false, false, false, false, false]);
    const [storyData, setStoryData] = useState<BranchedStoryType | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<boolean>(false);
    useEffect(() => {
        if (storyId) {
            setLoading(true);
            getById(storyId)
                .then((story) => {
                    setStoryData(story);
                    setError(false);
                })
                .catch(() => {
                    setError(true);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [storyId]);
    const handleStepComplete = (stepIndex: number) => {
        const newCompletedSteps = [...completedSteps];
        newCompletedSteps[stepIndex] = true;
        setCompletedSteps(newCompletedSteps);
        // Move to next step if not the last one
        if (stepIndex < completedSteps.length - 1) {
            setActiveStep(stepIndex + 1);
        }
    };
    const handleWizardComplete = () => {
        // Navigate back to the story view page
        push(`/curation/branched-stories/view/${storyId}`);
    };
    if (loading) {
        return (
            <Box p={4}>
                <Spinner />
            </Box>
        );
    }
    if (error || !storyData) {
        return (
            <Box p={4}>
                <Error description="Unable to load Branched Story for editing" />
            </Box>
        );
    }
    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={verbalTheme}>
                <Box p={3} style={{ backgroundColor: "#F9F9FB" }}>
                    <Box mb={2}>
                        <Breadcrumb
                            crumbs={[
                                { name: "Branched Stories", url: "/curation/branched-stories" },
                                { name: storyData.storyName, url: `/curation/branched-stories/view/${storyId}` },
                            ]}
                            current="Edit"
                        />
                    </Box>
                </Box>
                <BranchedStoryEditProvider initialStory={storyData}>
                    <Box p={4}>
                        <Container maxWidth="md">
                            <StepperWizard activeStep={activeStep} goToStep={setActiveStep} orientation="horizontal">
                                <WizardStep identifier={0} heading="Story Details" completed={completedSteps[0]}>
                                    <EditStoryDetails onComplete={() => handleStepComplete(0)} />
                                </WizardStep>
                                <WizardStep identifier={1} heading="Story Parts" completed={completedSteps[1]}>
                                    <EditStoryParts onComplete={() => handleStepComplete(1)} />
                                </WizardStep>
                                <WizardStep identifier={2} heading="Decision Points" completed={completedSteps[2]}>
                                    <EditDecisionPoints onComplete={() => handleStepComplete(2)} />
                                </WizardStep>
                                <WizardStep identifier={3} heading="Questions" completed={completedSteps[3]}>
                                    <EditQuestions onComplete={() => handleStepComplete(3)} />
                                </WizardStep>
                                <WizardStep identifier={4} heading="Review & Save" completed={completedSteps[4]}>
                                    <ReviewChanges onComplete={handleWizardComplete} />
                                </WizardStep>
                            </StepperWizard>
                        </Container>
                    </Box>
                </BranchedStoryEditProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};
// Display name for debugging (following design guide)
EditBranchedStoryWizard.displayName = "EditBranchedStoryWizard";
